import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Crosshair, ShieldCheck, Cpu, Languages } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const { login } = useAuth();
  const { lang, t, switchLang } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password, rememberMe);
      toast.success("Welcome to TrueLine");
      const next = searchParams.get("next");
      if (next && next.startsWith("/")) {
        navigate(next);
      } else {
        // Role-based landing: sales reps go directly to their workspace
        const role = user?.role || "";
        if (role === "sales") {
          navigate("/my-workspace");
        } else {
          navigate("/");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#030712] text-slate-100">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-slate-800/60">
        <div className="absolute inset-0 bg-grid-pattern" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950/40 via-transparent to-cyan-950/30" />
        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          <div className="flex items-center gap-3">
            <BrandLogo size="lg" />
            <div>
              <div className="font-display text-xl font-semibold tracking-tight">TrueLine</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-bold">Calibration Platform</div>
            </div>
          </div>

          <div className="space-y-7">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-bold mb-4">Enterprise ADAS Intelligence</div>
              <h1 className="font-display text-5xl xl:text-6xl font-semibold tracking-tight leading-[1.05]">
                Calibrate.<br />
                <span className="text-blue-500">Document.</span><br />
                <span className="text-cyan-400">Get Paid.</span>
              </h1>
              <p className="mt-6 text-slate-400 max-w-md text-sm leading-relaxed">
                The complete operating system for ADAS calibration shops and body shop partners. OEM intelligence, dual-invoicing, scan documentation, and AI-powered claim justification — in one platform.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[
                { icon: ShieldCheck, label: "OEM Compliant" },
                { icon: Cpu, label: "AI-Powered" },
                { icon: Crosshair, label: "25+ OEMs" },
              ].map((f) => (
                <div key={f.label} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <f.icon className="w-4 h-4 text-blue-500 mb-2" />
                  <div className="text-xs text-slate-300">{f.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-500 font-mono">v1.0.0 · {new Date().getFullYear()} TrueLine</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6 flex items-center gap-2">
          <button
            data-testid="lang-toggle-btn"
            onClick={() => switchLang(lang === "en" ? "pt" : "en")}
            className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 hover:text-blue-400 flex items-center gap-1.5 transition-colors"
          >
            <Languages className="w-3.5 h-3.5" />
            {lang === "en" ? "EN" : "PT"}
          </button>
        </div>
        <div className="w-full max-w-sm animate-slide-up">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <BrandLogo size="md" />
            <div className="font-display text-lg font-semibold">TrueLine</div>
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-bold mb-3">{t("login")}</div>
          <h2 className="font-display text-3xl font-semibold tracking-tight mb-2">{t("welcome_back")}</h2>
          <p className="text-sm text-slate-400 mb-8">{t("sign_in_to_continue")}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">{t("email")}</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-[#0B1120] border-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 h-11"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">{t("password")}</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-[#0B1120] border-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 h-11"
                required
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="login-remember-label">
              <input
                type="checkbox"
                data-testid="login-remember-checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 accent-blue-500"
              />
              <span className="text-xs text-slate-400">Lembrar neste dispositivo por 30 dias</span>
            </label>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-500 h-11 font-medium tracking-wide"
            >
              {loading ? "Signing in..." : t("login")}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-800 text-center">
            <p className="text-xs text-slate-500">
              Novo por aqui?{" "}
              <a href="/register" data-testid="register-link" className="text-blue-400 hover:text-blue-300 font-medium">
                Crie sua conta →
              </a>
            </p>
            <p className="text-[10px] text-slate-600 mt-1 italic">
              Você vai precisar de um código de convite fornecido pelo administrador.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
